import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  extractSeqFromResponse,
  getDateKey,
  getDateSeparatorLabel,
  getMessageTimestamp,
  sortMessagesBySeq,
} from "../../../../utils/tinodeMessageUtils";
import { RiArrowDownDoubleFill } from "react-icons/ri";
import { LuClock, LuImagePlus, LuSend } from "react-icons/lu";
import { sendMessage } from "../../../../services/tinodeApi";
import { renewTokenUser } from "../../../../redux/features/auth/authSlice";
import { Drafty } from "tinode-sdk";
import NotesMessageBubble, { isNotesMessage } from "./NotesMessageBubble";
import PrescriptionMessageBubble, {
  isPrescriptionMessage,
} from "./PrescriptionMessageBubble";

const ChatView = ({
  selectedTopic,
  tinodeRef,
  messages = [],
  messagesEndRef,
  messagesContainerRef,
  messageSeqSetRef,
  loadOlderMessages,
  setModalImageSrc,
  setShowImageModal,
  isSessionOver,
  fileInputRef,
  fullWidth = false,
  beforeSend,
  sessionWindow,
  isCustomer,
}) => {
  const dispatch = useDispatch();
  const lastMessageCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const unreadCountRef = useRef(0);
  const isLoadingOlderRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const scrollTimeoutRef = useRef(null);
  const lastScrollPositionRef = useRef(0);

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imagePreviewCaption, setImagePreviewCaption] = useState("");
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [inputText, setInputText] = useState("");
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    isInitialLoadRef.current = true;
    setShowScrollToBottom(false);
    setIsButtonVisible(false);
    setUnreadCount(0);
    unreadCountRef.current = 0;
    lastMessageCountRef.current = 0;
    isLoadingOlderRef.current = false;
    lastScrollTopRef.current = 0;
    lastScrollPositionRef.current = 0;
    setInputText("");
    setIsSending(false);

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  }, [selectedTopic]);

  useEffect(() => {
    const container = messagesContainerRef?.current;
    if (!container) return;

    const currentMessageCount = messages.length;
    const previousMessageCount = lastMessageCountRef.current;

    if (
      isInitialLoadRef.current &&
      currentMessageCount > 0 &&
      previousMessageCount === 0
    ) {
      lastMessageCountRef.current = currentMessageCount;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = container.scrollHeight;
            setShowScrollToBottom(false);
            setUnreadCount(0);
            unreadCountRef.current = 0;
          }
        });
      });

      isInitialLoadRef.current = false;
      return;
    }

    const hasNewMessages =
      currentMessageCount > previousMessageCount && previousMessageCount > 0;

    if (hasNewMessages && !isLoadingOlderRef.current) {
      const newUnreadCount = currentMessageCount - previousMessageCount;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 200;

      if (isNearBottom) {
        requestAnimationFrame(() => {
          if (container && !isLoadingOlderRef.current) {
            container.scrollTop = container.scrollHeight;
            setShowScrollToBottom(false);
            setUnreadCount(0);
            unreadCountRef.current = 0;
          }
        });
      } else {
        const updatedCount = unreadCountRef.current + newUnreadCount;
        unreadCountRef.current = updatedCount;
        setUnreadCount(updatedCount);
        setShowScrollToBottom(true);
        setIsButtonVisible(true);
      }
    }

    if (!isInitialLoadRef.current && currentMessageCount > 0) {
      lastMessageCountRef.current = currentMessageCount;
    }
  }, [messages.length, messages, messagesContainerRef]);

  useEffect(() => {
    const container = messagesContainerRef?.current;
    if (!container) return;

    const handleScroll = () => {
      if (isLoadingOlderRef.current) {
        const { scrollTop } = container;
        if (scrollTop > lastScrollPositionRef.current) {
          container.scrollTop = lastScrollPositionRef.current;
          return;
        }
        lastScrollPositionRef.current = scrollTop;
        return;
      }

      if (messages.length === 0) {
        setShowScrollToBottom(false);
        setIsButtonVisible(false);
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isAtBottom = distanceFromBottom < 50;
      const isNearTop = scrollTop < 200;
      const isScrollingUp = scrollTop < lastScrollTopRef.current;
      lastScrollTopRef.current = scrollTop;
      lastScrollPositionRef.current = scrollTop;

      if (
        isNearTop &&
        isScrollingUp &&
        !isLoadingOlderRef.current &&
        loadOlderMessages
      ) {
        const minSeq =
          messages.length > 0 && messages[0].seq ? messages[0].seq : null;
        if (minSeq && typeof minSeq === "number" && minSeq > 1) {
          isLoadingOlderRef.current = true;
          setIsLoadingOlder(true);

          const scrollTopBefore = container.scrollTop;
          const scrollHeightBefore = container.scrollHeight;
          lastScrollPositionRef.current = scrollTopBefore;

          loadOlderMessages(selectedTopic, minSeq)
            .then(() => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  if (container) {
                    const heightDiff =
                      container.scrollHeight - scrollHeightBefore;
                    if (heightDiff > 0) {
                      container.scrollTop = scrollTopBefore + heightDiff;
                      lastScrollPositionRef.current = container.scrollTop;
                    }
                  }
                  isLoadingOlderRef.current = false;
                  setIsLoadingOlder(false);
                });
              });
            })
            .catch(() => {
              isLoadingOlderRef.current = false;
              setIsLoadingOlder(false);
            });
        }
      }

      // Show/hide scroll to bottom button
      setShowScrollToBottom(!isAtBottom);
      if (!isAtBottom) {
        setIsButtonVisible(true);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
          if (unreadCountRef.current === 0) {
            setIsButtonVisible(false);
            setTimeout(() => setShowScrollToBottom(false), 300);
          }
        }, 1000);
      } else {
        if (unreadCountRef.current === 0) {
          setIsButtonVisible(false);
          setShowScrollToBottom(false);
        }
        if (unreadCountRef.current > 0) {
          setUnreadCount(0);
          unreadCountRef.current = 0;
        }
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [messages.length, loadOlderMessages, selectedTopic, messagesContainerRef]);

  const scrollToBottom = () => {
    const container = messagesContainerRef?.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      setTimeout(() => {
        setUnreadCount(0);
        unreadCountRef.current = 0;
        setShowScrollToBottom(false);
      }, 100);
    }
  };

  const handleImageClick = (imageSrc, imageMime) => {
    setModalImageSrc?.(`data:${imageMime};base64,${imageSrc}`);
    setShowImageModal?.(true);
  };

  // Kirim pesan teks — langsung kirim ke server (sama seperti Chats.jsx, tidak pakai optimistic)
  const handleSendText = useCallback(async () => {
    const text = inputText.trim();
    // FIX: Guard untuk mencegah double click
    if (
      !text ||
      !selectedTopic ||
      !tinodeRef?.current ||
      isSessionOver ||
      isSending
    )
      return;

    setIsSending(true);
    setInputText("");

    try {
      if (typeof beforeSend === "function") {
        await beforeSend(selectedTopic);
      }

      // FIX: Langsung kirim ke server tanpa optimistic (sama seperti Chats.jsx)
      const sentMessage = await sendMessage(
        tinodeRef.current,
        selectedTopic,
        text,
      );
      const sentSeq = extractSeqFromResponse(sentMessage);

      if (sentSeq) {
        messageSeqSetRef.current.add(sentSeq);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Jika error, kembalikan text ke input
      setInputText(text);
    } finally {
      // Reset isSending setelah delay kecil untuk mencegah race condition
      setTimeout(() => {
        setIsSending(false);
      }, 300);
    }
  }, [
    inputText,
    selectedTopic,
    tinodeRef,
    isSessionOver,
    isSending,
    messageSeqSetRef,
    beforeSend,
  ]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  const compressImage = async (
    file,
    maxSizeKB = 80,
    maxWidth = 1024,
    maxHeight = 1024,
  ) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Resize jika gambar terlalu besar (jaga aspect ratio)
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          // Coba berbagai quality sampai dapat ukuran < maxSizeKB
          let quality = 0.9;
          let attempts = 0;
          const maxAttempts = 15;

          const tryCompress = () => {
            canvas.toBlob(
              (compressedBlob) => {
                if (!compressedBlob) {
                  reject(new Error("Failed to compress image"));
                  return;
                }

                const sizeKB = compressedBlob.size / 1024;

                // Jika sudah di bawah maxSizeKB atau sudah terlalu banyak attempt atau quality terlalu rendah
                if (
                  sizeKB <= maxSizeKB ||
                  attempts >= maxAttempts ||
                  quality <= 0.1
                ) {
                  // Convert blob ke base64
                  const reader2 = new FileReader();
                  reader2.onload = () => {
                    const base64String = reader2.result.split(",")[1];
                    resolve({
                      base64: base64String,
                      blob: compressedBlob,
                      width: width,
                      height: height,
                      size: compressedBlob.size,
                      quality: quality,
                    });
                  };
                  reader2.onerror = () =>
                    reject(
                      new Error("Failed to convert compressed image to base64"),
                    );
                  reader2.readAsDataURL(compressedBlob);
                } else {
                  // Kurangi quality dan coba lagi
                  quality -= 0.05;
                  attempts++;
                  tryCompress();
                }
              },
              // FIX: Untuk kompresi, selalu gunakan JPEG untuk ukuran lebih kecil
              // PNG tidak bisa di-compress dengan quality, jadi convert ke JPEG
              "image/jpeg",
              quality,
            );
          };

          tryCompress();
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const imageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // FIX: Validasi ukuran file maksimal 12MB - tolak langsung jika lebih besar
    const MAX_INPUT_FILE_SIZE_MB = 12; // 12MB
    const MAX_INPUT_FILE_SIZE = MAX_INPUT_FILE_SIZE_MB * 1024 * 1024; // dalam bytes
    if (file.size > MAX_INPUT_FILE_SIZE) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      alert(
        `Gambar terlalu besar untuk diproses!\n\n` +
          `Ukuran file: ${fileSizeMB} MB\n` +
          `Ukuran maksimal: ${MAX_INPUT_FILE_SIZE_MB} MB\n\n` +
          `Silakan pilih gambar yang lebih kecil.`,
      );
      // Reset file input
      if (e.target) {
        e.target.value = "";
      }
      return;
    }

    const MAX_FILE_SIZE_KB = 80; // 80KB
    const MAX_FILE_SIZE = MAX_FILE_SIZE_KB * 1024; // dalam bytes
    const MAX_FILE_SIZE_MB = (MAX_FILE_SIZE_KB / 1024).toFixed(2); // untuk display

    try {
      let processedFile = file;
      let base64 = "";
      let width = 0;
      let height = 0;
      let finalSize = file.size;

      // Jika file > 80KB, kompres
      if (file.size > MAX_FILE_SIZE) {
        const originalSizeMB = (file.size / 1024 / 1024).toFixed(2);
        const originalSizeKB = (file.size / 1024).toFixed(2);
        const compressed = await compressImage(file, MAX_FILE_SIZE_KB);
        base64 = compressed.base64;
        width = compressed.width;
        height = compressed.height;
        finalSize = compressed.size;

        // FIX: Gunakan mime type dari blob yang sudah dikompres (bisa berbeda dari file asli)
        const compressedMime =
          compressed.blob.type || file.type || "image/jpeg";

        // Buat File object baru dari blob untuk preview
        processedFile = new File([compressed.blob], file.name, {
          type: compressedMime,
          lastModified: Date.now(),
        });
      } else {
        // File sudah kecil, tidak perlu kompres
        const dimensions = await getImageDimensions(file);
        width = dimensions.width;
        height = dimensions.height;
        base64 = await imageToBase64(file);
      }

      // FIX: Pastikan mime type sesuai dengan file yang sudah diproses
      const finalMime = processedFile.type || file.type || "image/jpeg";

      setImagePreview({
        file: processedFile,
        base64,
        width,
        height,
        mime: finalMime,
        name: processedFile.name,
        size: finalSize,
      });
      setImagePreviewCaption("");
    } catch (err) {
      console.error("Error processing image:", err);
      alert(err.message || "Failed to process image");
      // Reset file input jika error
      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleSendImage = async () => {
    if (!imagePreview || !tinodeRef?.current || !selectedTopic || isSessionOver)
      return;

    // FIX: Double check ukuran file sebelum kirim (max 80KB)
    const MAX_FILE_SIZE_KB = 80;
    const MAX_FILE_SIZE = MAX_FILE_SIZE_KB * 1024; // 80KB dalam bytes
    if (imagePreview.size > MAX_FILE_SIZE) {
      const fileSizeKB = (imagePreview.size / 1024).toFixed(2);
      const fileSizeMB = (imagePreview.size / (1024 * 1024)).toFixed(2);
      alert(
        `Gambar terlalu besar untuk dikirim!\n\n` +
          `Ukuran file: ${fileSizeKB} KB (${fileSizeMB} MB)\n` +
          `Ukuran maksimal: ${MAX_FILE_SIZE_KB} KB\n\n` +
          `Silakan pilih gambar yang lebih kecil. Gambar akan dikompres otomatis jika melebihi ${MAX_FILE_SIZE_KB} KB.`,
      );
      return;
    }

    try {
      setIsUploadingImage(true);

      if (typeof beforeSend === "function") {
        await beforeSend(selectedTopic);
      }

      // FIX: Pastikan base64 tidak ada prefix data:image/...;base64,
      let base64Data = imagePreview.base64;
      if (base64Data && typeof base64Data === "string") {
        // Hapus prefix jika ada
        if (base64Data.startsWith("data:")) {
          const commaIndex = base64Data.indexOf(",");
          if (commaIndex !== -1) {
            base64Data = base64Data.substring(commaIndex + 1);
          }
        }
      }

      // FIX: Validasi base64 sebelum kirim
      if (!base64Data || base64Data.length === 0) {
        throw new Error(
          "Base64 image data is empty. Please select the image again.",
        );
      }

      // FIX: Cek ukuran base64 - base64 ~33% lebih besar dari file size
      // maxMessageSize dari server: 131072 bytes (128KB)
      // Target: file < 80KB, base64 akan ~107KB, masih dalam limit
      const MAX_MESSAGE_SIZE = 128 * 1024; // 128KB
      const MAX_BASE64_SIZE = 80 * 1024; // 80KB untuk base64 (lebih aman)
      const base64Size = base64Data.length;

      // FIX: Jika base64 terlalu besar, re-compress dengan target lebih kecil
      if (base64Size > MAX_BASE64_SIZE) {
        // Target file size: 60KB (base64 akan ~80KB)
        const targetFileSizeKB = 60;
        console.warn(
          `⚠️ Base64 size (${(base64Size / 1024).toFixed(2)} KB) exceeds limit (${(MAX_BASE64_SIZE / 1024).toFixed(2)} KB). Re-compressing to ${targetFileSizeKB} KB...`,
        );

        try {
          const recompressed = await compressImage(
            imagePreview.file,
            targetFileSizeKB,
            1024,
            1024,
          );
          base64Data = recompressed.base64;

          // Update imagePreview dengan data yang sudah di-compress ulang
          imagePreview.base64 = base64Data;
          imagePreview.size = recompressed.size;
          imagePreview.width = recompressed.width;
          imagePreview.height = recompressed.height;
          imagePreview.mime = recompressed.blob.type || imagePreview.mime;
        } catch (recompressErr) {
          console.error("❌ Re-compression failed:", recompressErr);
          throw new Error(
            `Gambar terlalu besar. Gagal mengompres ke ${targetFileSizeKB}KB. Silakan pilih gambar yang lebih kecil.`,
          );
        }
      }

      // FIX: Pastikan mime type valid
      const imageMime = imagePreview.mime || "image/jpeg";

      let draftyMessage;
      try {
        draftyMessage = Drafty.insertImage(null, 0, {
          mime: imageMime,
          bits: base64Data, // Gunakan base64 tanpa prefix
          width: imagePreview.width,
          height: imagePreview.height,
          filename: imagePreview.name,
          size: imagePreview.size,
        });
      } catch (draftyErr) {
        console.error("❌ Failed to create Drafty message:", draftyErr);
        throw new Error(
          `Failed to prepare image: ${draftyErr.message || draftyErr}`,
        );
      }

      if (imagePreviewCaption.trim()) {
        draftyMessage = Drafty.appendLineBreak(draftyMessage);
        draftyMessage = Drafty.append(
          draftyMessage,
          Drafty.parse(imagePreviewCaption.trim()),
        );
      }

      // FIX: Langsung kirim ke server tanpa optimistic (sama seperti Chats.jsx)
      let lastError = null;
      let retryCount = 0;
      const maxRetries = 2;

      const attemptSend = async () => {
        try {
          const result = await sendMessage(
            tinodeRef.current,
            selectedTopic,
            draftyMessage,
          );

          // FIX: Extract seq dan tambahkan ke messageSeqSetRef (sama seperti Chats.jsx)
          const sentSeq = extractSeqFromResponse(result);
          if (sentSeq) {
            messageSeqSetRef.current.add(sentSeq);
          }

          return null; // Success
        } catch (sendErr) {
          // FIX: Handle 503 error - reconnect and retry
          const is503Error =
            sendErr?.code === 503 ||
            sendErr?.text === "Websocket is not connected" ||
            sendErr?.message?.includes("Websocket is not connected") ||
            sendErr?.message?.includes("503");

          if (is503Error && retryCount < maxRetries) {
            console.warn(
              `⚠️ Websocket not connected (503) when sending image, attempting to reconnect... (attempt ${retryCount + 1}/${maxRetries})`,
            );
            retryCount++;

            try {
              const { connectTinodeInstance, attemptTinodeRelogin } =
                await import("../../../../services/tinodeSingleton");

              if (!tinodeRef.current.isConnected()) {
                await connectTinodeInstance(tinodeRef.current);
              }

              if (!tinodeRef.current.isAuthenticated()) {
                await attemptTinodeRelogin(tinodeRef.current);
              }

              // Wait for connection to stabilize
              await new Promise((r) => setTimeout(r, 500));

              if (
                tinodeRef.current.isConnected() &&
                tinodeRef.current.isAuthenticated()
              ) {
                console.log("✅ Reconnected, retrying send image...");
                // Retry send
                return await attemptSend();
              } else {
                throw new Error("Failed to reconnect WebSocket");
              }
            } catch (reconnectErr) {
              console.error("❌ Reconnect failed:", reconnectErr);
              const err = new Error(
                "WebSocket connection lost. Please try again.",
              );
              err.code = 503;
              err.originalError = reconnectErr;
              return err;
            }
          }

          // FIX: Handle 401 error - re-authenticate and retry
          if (
            (sendErr?.code === 401 ||
              sendErr?.message?.includes("authentication")) &&
            retryCount < maxRetries
          ) {
            console.warn(
              `⚠️ Authentication expired (401), attempting to re-authenticate... (attempt ${retryCount + 1}/${maxRetries})`,
            );
            retryCount++;

            try {
              dispatch(renewTokenUser());
              const { attemptTinodeRelogin } =
                await import("../../../../services/tinodeSingleton");
              const relogged = await attemptTinodeRelogin(tinodeRef.current);

              if (relogged && tinodeRef.current.isAuthenticated()) {
                console.log("✅ Re-authenticated, retrying send image...");
                // Retry send
                return await attemptSend();
              } else {
                throw new Error("Re-authentication failed");
              }
            } catch (authErr) {
              console.error("❌ Re-authentication failed:", authErr);
              const err = new Error(
                "Authentication expired. Please refresh the page.",
              );
              err.code = 401;
              err.originalError = authErr;
              return err;
            }
          }

          // FIX: Handle network errors - reconnect and retry
          const isNetworkError =
            sendErr?.code === 500 ||
            sendErr?.code === 502 ||
            sendErr?.code === 504 ||
            sendErr?.message?.includes("timeout") ||
            sendErr?.message?.includes("network") ||
            sendErr?.message?.includes("ECONNREFUSED") ||
            sendErr?.message?.includes("Failed to fetch");

          if (isNetworkError && retryCount < maxRetries) {
            console.warn(
              `⚠️ Network error when sending image, attempting to reconnect... (attempt ${retryCount + 1}/${maxRetries})`,
            );
            retryCount++;

            try {
              const { connectTinodeInstance, attemptTinodeRelogin } =
                await import("../../../../services/tinodeSingleton");

              if (!tinodeRef.current.isConnected()) {
                await connectTinodeInstance(tinodeRef.current);
              }

              if (!tinodeRef.current.isAuthenticated()) {
                await attemptTinodeRelogin(tinodeRef.current);
              }

              // Wait for connection to stabilize
              await new Promise((r) => setTimeout(r, 1000));

              if (
                tinodeRef.current.isConnected() &&
                tinodeRef.current.isAuthenticated()
              ) {
                console.log(
                  "✅ Reconnected after network error, retrying send image...",
                );
                // Retry send
                return await attemptSend();
              } else {
                throw new Error("Failed to reconnect after network error");
              }
            } catch (reconnectErr) {
              console.error(
                "❌ Reconnect after network error failed:",
                reconnectErr,
              );
              return sendErr; // Return original error
            }
          }

          // Return original error if no retry logic applies
          return sendErr;
        }
      };

      lastError = await attemptSend();
      if (lastError) throw lastError;

      setImagePreview(null);
      setImagePreviewCaption("");
      if (fileInputRef?.current) fileInputRef.current.value = "";

      setTimeout(() => {
        const container = messagesContainerRef?.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
          setShowScrollToBottom(false);
        }
      }, 100);
    } catch (err) {
      // FIX: Tidak perlu hapus optimistic karena tidak ada optimistic messages

      const errCode = err?.code;
      const errText = err?.text || err?.message || String(err);

      let errorMsg = "Gagal mengirim gambar";

      // FIX: Handle berbagai error dari Tinode dengan pesan yang lebih jelas
      if (errCode === 401) {
        errorMsg =
          "Sesi telah berakhir. Silakan refresh halaman dan coba lagi.";
      } else if (
        errCode === 403 ||
        errText?.includes("permission denied") ||
        errText?.includes("forbidden")
      ) {
        errorMsg = "Anda tidak memiliki izin untuk mengirim pesan di chat ini.";
      } else if (
        errCode === 413 ||
        errText?.includes("too large") ||
        errText?.includes("Payload too large")
      ) {
        errorMsg =
          "Gambar terlalu besar (maksimal 80KB). Silakan pilih gambar yang lebih kecil.";
      } else if (
        errCode === 503 ||
        errText?.includes("Websocket is not connected") ||
        errText?.includes("connection lost")
      ) {
        errorMsg =
          "Koneksi terputus. Silakan coba kirim ulang setelah koneksi kembali.";
      } else if (
        errCode === 504 ||
        errText?.includes("timeout") ||
        errText?.includes("Promise expired")
      ) {
        errorMsg =
          "Upload gambar timeout. Silakan coba lagi dengan gambar yang lebih kecil atau periksa koneksi internet Anda.";
      } else if (
        errCode === 500 ||
        errCode === 502 ||
        errText?.includes("Internal Server Error") ||
        errText?.includes("Bad Gateway")
      ) {
        errorMsg =
          "Server mengalami masalah. Silakan coba lagi dalam beberapa saat.";
      } else if (errText?.includes("rejected") || errText?.includes("reject")) {
        errorMsg =
          "Gambar ditolak oleh server. Silakan coba dengan gambar lain.";
      } else if (
        errText?.includes("network") ||
        errText?.includes("ECONNREFUSED") ||
        errText?.includes("Failed to fetch")
      ) {
        errorMsg =
          "Masalah koneksi jaringan. Silakan periksa koneksi internet Anda dan coba lagi.";
      } else if (errText?.includes("crash") || errText?.includes("crashed")) {
        errorMsg = "Terjadi kesalahan saat mengirim gambar. Silakan coba lagi.";
      } else if (err?.text) {
        errorMsg = `Gagal mengirim gambar: ${err.text}`;
      } else if (err?.message) {
        errorMsg = `Gagal mengirim gambar: ${err.message}`;
      } else {
        errorMsg = `Gagal mengirim gambar: ${String(err)}`;
      }

      console.error("❌ Failed to send image:", {
        code: errCode,
        text: errText,
        originalError: err,
        stack: err?.stack,
      });

      alert(errorMsg);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleCancelImage = () => {
    setImagePreview(null);
    setImagePreviewCaption("");
    if (fileInputRef?.current) fileInputRef.current.value = "";
  };

  const renderMessages = () => {
    if (messages.length === 0) {
      return (
        <div className="text-center text-gray-400 mt-[50px]">
          <p>Mulai percakapan!</p>
        </div>
      );
    }

    // De-duplikasi — preferensikan pesan non-optimistic
    const uniqueMessages = new Map();
    messages.forEach((msg) => {
      if (msg.seq) {
        const existing = uniqueMessages.get(msg.seq);
        if (!existing) {
          uniqueMessages.set(msg.seq, msg);
        } else if (existing.isOptimistic && !msg.isOptimistic) {
          uniqueMessages.set(msg.seq, msg);
        }
      }
    });

    const sortedMessages = sortMessagesBySeq(
      Array.from(uniqueMessages.values()),
    );
    const renderedItems = [];
    let lastDateKey = null;

    sortedMessages.forEach((msg, index) => {
      const msgTimestamp = getMessageTimestamp(msg);
      const currentDateKey = getDateKey(msgTimestamp);

      // Date separator
      if (currentDateKey !== lastDateKey) {
        const dateLabel = getDateSeparatorLabel(msgTimestamp);
        renderedItems.push(
          <div
            key={`date-separator-${currentDateKey}`}
            className="flex justify-center items-center my-5 w-full"
          >
            <div className="py-1.5 px-3 bg-[#E8E8E8] rounded-[12px] text-xs text-gray-600 font-medium text-center min-w-[80px]">
              {dateLabel}
            </div>
          </div>,
        );
        lastDateKey = currentDateKey;
      }

      const isOwn = msg.from === tinodeRef?.current?.getCurrentUserID();
      const displayTime = msg.ts
        ? new Date(msg.ts).toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : msg.timestamp
          ? new Date(msg.timestamp).toLocaleTimeString("id-ID", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "";

      // Ambil teks konten pesan
      const msgContent =
        msg.data ||
        (typeof msg.content === "object" && msg.content?.ent
          ? msg.content
          : {});
      const rawText =
        typeof msg.content === "string" ? msg.content : msgContent.txt || "";

      // Cek apakah pesan bergambar
      const hasImage =
        msgContent?.ent &&
        Array.isArray(msgContent.ent) &&
        msgContent.ent.some((ent) => ent.tp === "IM" || ent.tp === "EX");

      // ── GREETING MESSAGE ─────────────────────────────────────────
      if (isNotesMessage(rawText) && !hasImage) {
        renderedItems.push(
          <div
            key={`${msg.seq}_${index}`}
            data-message-seq={msg.seq}
            className={`flex ${isOwn ? "justify-end" : "justify-start"} mb-3`}
          >
            <NotesMessageBubble
              text={rawText}
              isFromMe={isOwn}
              time={displayTime}
            />
          </div>,
        );
        return;
      }

      if (isPrescriptionMessage(rawText) && !hasImage) {
        renderedItems.push(
          <div
            key={`msg-${msg.seq}`}
            className={`flex ${isOwn ? "justify-end" : "justify-start"} mb-3`}
          >
            <PrescriptionMessageBubble
              text={rawText}
              isFromMe={isOwn}
              time={displayTime}
            />
          </div>,
        );
        return; // skip render bubble normal
      }

      // ── PESAN GAMBAR ─────────────────────────────────────────────
      if (hasImage) {
        renderedItems.push(
          <div
            key={`${msg.seq}_${index}`}
            data-message-seq={msg.seq}
            className={`flex ${isOwn ? "justify-end" : "justify-start"} mb-1`}
          >
            <div
              className={`max-w-[70%] py-2 px-2.5 shadow-sm break-words ${
                isOwn
                  ? "bg-secondary text-white rounded-tl-xl rounded-bl-xl rounded-tr-xl"
                  : "bg-white text-slate-700 rounded-tr-xl rounded-br-xl rounded-bl-xl"
              }`}
            >
              {msgContent.ent.map((ent, entIndex) => {
                if (ent.tp === "IM" && ent.data) {
                  const imageData = ent.data;
                  const imageSrc =
                    imageData.bits || imageData.val || imageData.ref;
                  const imageMime = imageData.mime || "image/png";
                  const imageWidth = imageData.width || 300;
                  return (
                    <div key={entIndex} className="mb-2">
                      <img
                        src={
                          imageSrc
                            ? imageSrc.startsWith("data:")
                              ? imageSrc
                              : `data:${imageMime};base64,${imageSrc}`
                            : ""
                        }
                        alt="Sent"
                        onClick={() =>
                          imageSrc && handleImageClick(imageSrc, imageMime)
                        }
                        className="max-w-full max-h-[400px] rounded-lg cursor-pointer"
                        style={{
                          width: imageWidth > 300 ? "300px" : `${imageWidth}px`,
                          height: "auto",
                        }}
                      />
                      {imageData.name && (
                        <div className="text-[9px] opacity-50 mt-1">
                          {imageData.name}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })}
              {msgContent.txt && msgContent.txt.trim() && (
                <div className="mt-2 text-sm">{msgContent.txt}</div>
              )}
            </div>
          </div>,
        );
        return;
      }

      // ── PESAN TEKS BIASA ──────────────────────────────────────────
      renderedItems.push(
        <div
          key={`${msg.seq}_${index}`}
          data-message-seq={msg.seq}
          className={`flex ${isOwn ? "justify-end" : "justify-start"} mb-1`}
        >
          <div
            className={`max-w-[70%] py-2 px-2.5 shadow-sm break-words ${
              isOwn
                ? "bg-secondary text-white rounded-tl-xl rounded-bl-xl rounded-tr-xl"
                : "bg-white text-slate-700 rounded-tr-xl rounded-br-xl rounded-bl-xl"
            }`}
          >
            <div className="mb-1 text-md">{rawText || "Message"}</div>
            <div className="text-sm opacity-70 text-right">{displayTime}</div>
          </div>
        </div>,
      );
    });

    return renderedItems;
  };

  return (
    <div
      className={`${fullWidth ? "w-full" : "w-3/5"} flex flex-col h-full min-h-0`}
    >
      <div className="relative flex-1 min-h-0 flex flex-col">
        {isLoadingOlder && (
          <div className="absolute top-2.5 left-1/2 transform -translate-x-1/2 z-[100] flex items-center justify-center bg-white/95 rounded-[20px] py-1.5 px-3.5 shadow-lg pointer-events-none">
            <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
            <span className="text-xs text-blue-500 font-medium">
              Memuat pesan lama...
            </span>
          </div>
        )}

        <div
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 bg-slate-100 relative"
          ref={messagesContainerRef}
          style={{ scrollBehavior: "auto" }}
        >
          <div className="flex flex-col gap-1">
            {renderMessages()}
            <div ref={messagesEndRef} className="h-px" />
          </div>
        </div>

        {(showScrollToBottom || unreadCount > 0) && (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[1000]">
            <button
              onClick={scrollToBottom}
              className="flex items-center gap-1.5 bg-white shadow-lg rounded-full py-1.5 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-all"
              style={{
                opacity: isButtonVisible ? 1 : 0,
                pointerEvents: isButtonVisible ? "auto" : "none",
              }}
            >
              <RiArrowDownDoubleFill size={16} className="text-primary" />
              {unreadCount > 0
                ? `${unreadCount > 99 ? "99+" : unreadCount} pesan baru`
                : "Ke bawah"}
            </button>
          </div>
        )}
      </div>

      {!isSessionOver ? (
        <div className="h-14 min-h-[52px] flex items-center bg-white border-t flex-shrink-0 px-2 py-1.5">
          <div className="flex gap-1.5 items-center w-full">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            <div
              onClick={() => fileInputRef?.current?.click()}
              className="p-1.5 cursor-pointer rounded-full transition duration-300 text-slate-500 hover:text-primary flex-shrink-0"
            >
              {isUploadingImage ? (
                <LuClock size={20} />
              ) : (
                <LuImagePlus size={22} />
              )}
            </div>
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Tulis pesan..."
              disabled={isSending}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-full text-sm outline-none focus:border-secondary focus:ring-1 focus:ring-secondary transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <div
              onClick={handleSendText}
              className={`p-1.5 rounded-full transition duration-300 flex-shrink-0 ${
                inputText.trim() && !isSending
                  ? "text-primary hover:bg-primary/10 cursor-pointer"
                  : "text-slate-300 cursor-not-allowed"
              }`}
            >
              <LuSend size={22} />
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-orange-50 border-t border-orange-200 px-4 h-14 min-h-[52px] flex w-full items-center justify-center flex-shrink-0">
          <p className="text-orange-600 text-sm">
            Sesi telah berakhir. Chat tidak dapat dilanjutkan.
          </p>
        </div>
      )}

      {imagePreview && (
        <div className="fixed inset-0 bg-black/80 flex flex-col items-center justify-center z-[99999] p-5">
          <div className="bg-white rounded-[12px] max-w-[90%] max-h-[90%] flex flex-col overflow-hidden">
            <div className="relative max-h-[60vh] overflow-auto flex items-center justify-center bg-gray-100">
              <img
                src={`data:${imagePreview.mime};base64,${imagePreview.base64}`}
                alt={imagePreview.name}
                className="max-w-full max-h-[60vh] object-contain"
              />
            </div>
            <div className="p-4 border-t border-gray-200">
              <input
                type="text"
                placeholder="Tambah keterangan (opsional)..."
                value={imagePreviewCaption}
                onChange={(e) => setImagePreviewCaption(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-[20px] text-sm outline-none mb-2.5"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendImage();
                  }
                }}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={handleCancelImage}
                  className="px-5 py-2 bg-gray-100 text-slate-700 border-none rounded-[20px] cursor-pointer text-md font-medium hover:bg-gray-200"
                >
                  Batal
                </button>
                <button
                  onClick={handleSendImage}
                  disabled={isUploadingImage}
                  className={`px-5 py-2 text-white border-none rounded-[20px] cursor-pointer text-md font-medium ${
                    isUploadingImage
                      ? "bg-gray-300 cursor-default"
                      : "bg-secondary hover:secondaryHover"
                  }`}
                >
                  {isUploadingImage ? "Mengirim..." : "Kirim"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatView;
